<template>
    <v-scale-transition>
        <v-card v-if="isRecording && isMinimized" class="recorder-overlay elevation-12">
            <!-- Video Preview -->
            <div class="video-preview-container" @click="handleRestore">
                <video ref="videoPreview" autoplay muted playsinline class="video-preview"></video>
                <div class="video-overlay">
                    <div class="d-flex align-center">
                        <div class="pulse-dot mr-2"></div>
                        <span class="recording-label">REC {{ formattedTime }}</span>
                    </div>
                </div>
            </div>

            <!-- Controls -->
            <v-card-text class="pa-2">
                <div class="d-flex align-center justify-space-between">
                    <!-- Folder Info -->
                    <div class="folder-info text-truncate flex-grow-1 mr-2">
                        <v-icon x-small color="grey darken-1">mdi-folder</v-icon>
                        <span class="folder-text ml-1">{{ folderName }}</span>
                    </div>

                    <!-- Actions -->
                    <div class="d-flex">
                        <v-btn icon x-small color="primary" @click.stop="handleRestore" class="mr-1">
                            <v-icon small>mdi-arrow-expand</v-icon>
                        </v-btn>
                        <v-btn icon x-small color="red" @click.stop="handleStop">
                            <v-icon small>mdi-stop</v-icon>
                        </v-btn>
                    </div>
                </div>
            </v-card-text>
        </v-card>
    </v-scale-transition>
</template>

<script>
import { mapGetters, mapActions, mapState } from 'vuex';

export default {
    name: 'RecorderOverlay',
    computed: {
        ...mapGetters('recorder', ['isRecording', 'isMinimized', 'folderName', 'elapsedTime']),
        ...mapState('recorder', ['stream']),
        formattedTime() {
            const minutes = Math.floor(this.elapsedTime / 60);
            const seconds = this.elapsedTime % 60;
            return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
        },
    },
    watch: {
        stream(newStream) {
            if (newStream && this.$refs.videoPreview) {
                this.$refs.videoPreview.srcObject = newStream;
            }
        },
        isMinimized(val) {
            if (val && this.stream) {
                this.$nextTick(() => {
                    if (this.$refs.videoPreview) {
                        this.$refs.videoPreview.srcObject = this.stream;
                    }
                });
            }
        },
        isRecording(val) {
            // When recording starts or minimized overlay appears, set the stream
            if (val && this.isMinimized && this.stream) {
                this.$nextTick(() => {
                    if (this.$refs.videoPreview) {
                        this.$refs.videoPreview.srcObject = this.stream;
                    }
                });
            }
        },
    },
    mounted() {
        // Set stream if already recording and minimized when component mounts
        if (this.isRecording && this.isMinimized && this.stream) {
            this.$nextTick(() => {
                if (this.$refs.videoPreview) {
                    this.$refs.videoPreview.srcObject = this.stream;
                }
            });
        }
    },
    methods: {
        ...mapActions('recorder', ['stopRecording', 'restoreRecorder']),
        handleRestore() {
            this.restoreRecorder();
            if (this.$route.name !== 'screenRecording') {
                this.$router.push({ name: 'screenRecording' });
            }
        },
        handleStop() {
            this.stopRecording();
        },
    },
};
</script>

<style scoped>
.recorder-overlay {
    position: fixed;
    bottom: 24px;
    right: 24px;
    z-index: 9999;
    width: 320px;
    background: #ffffff;
    border-radius: 12px;
    overflow: hidden;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.25);
}

.recorder-overlay:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.35) !important;
}

.video-preview-container {
    position: relative;
    width: 100%;
    height: 180px;
    background: #000;
    cursor: pointer;
    overflow: hidden;
}

.video-preview {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.video-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(180deg, rgba(0, 0, 0, 0.6) 0%, rgba(0, 0, 0, 0) 40%, rgba(0, 0, 0, 0.4) 100%);
    padding: 12px;
    pointer-events: none;
}

.pulse-dot {
    width: 8px;
    height: 8px;
    background: #f44336;
    border-radius: 50%;
    animation: pulse 1.5s ease-in-out infinite;
}

@keyframes pulse {

    0%,
    100% {
        transform: scale(1);
        opacity: 1;
    }

    50% {
        transform: scale(1.5);
        opacity: 0.7;
    }
}

.recording-label {
    color: #ffffff;
    font-weight: 700;
    font-size: 13px;
    letter-spacing: 1px;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);

}

.folder-info {
    display: flex;
    align-items: center;
    min-width: 0;
}

.folder-text {
    font-size: 12px;
    color: #616161;
    font-weight: 500;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

/* Responsive */
@media (max-width: 768px) {
    .recorder-overlay {
        bottom: 80px;
        right: 16px;
        width: 280px;
    }

    .video-preview-container {
        height: 160px;
    }
}
</style>
